import { useVirtualizer } from "@tanstack/react-virtual";
import { ChevronDown, ChevronUp } from "lucide-react";
import React, { useMemo, useRef } from "react";

import type { MpCode } from "@/api/orders";
import type { ArticleAttrs } from "@/api/orders-planning";
import { cn } from "@/lib/utils";
import { clusterRowSignal, type WarehouseAggRow } from "./clusters";
import { fmtDate, int0, monthLabel, signed } from "./format";
import {
  heatClass,
  marginPct,
  recommendation,
  rowMetrics,
  stockDayRange,
  type RowMetrics,
  type ViewContext,
  type ViewRow,
} from "./metrics";
import { Sparkline } from "./Sparkline";

export type TableMode = "history" | "price" | "margin" | "compact";

export interface SortState {
  key: string;
  dir: "desc" | "asc";
}

/** Интеграции v2 в матрицу: атрибуты, подсветки, кластеры, контроль, сравнение, bulk. */
export interface MatrixExtras {
  attrs: Map<string, ArticleAttrs>;
  charColors: Map<string, string>;
  highlights: Map<string, string>;
  clusters: {
    byArticle: Map<string, WarehouseAggRow[]>;
    rules: ReturnType<typeof import("./clusters").expandRules>;
    selectedDays: number;
  } | null;
  control: {
    set: Set<string>;
    mp: MpCode;
    toggle: (
      row: { article: string; name: string; brand: string; code_1c: string; id_1c: string },
      checked: boolean,
    ) => void;
  } | null;
  compare: { set: Set<string>; toggle: (key: string) => void };
  bulk: { set: Set<string>; toggle: (article: string) => void };
  /** План текущего месяца по артикулу (с учётом вкладки) — колонки «План мес»/«Вып.». */
  plan: Map<string, number> | null;
  canEdit: boolean;
  onComment: (article: string, comment: string) => void;
}

const ROW_H = 44;
const MONTH_W = 92;
const YEAR_TOTAL_W = 100;
const TOTAL_W = 104;

const HEAT_CLS: Record<string, string> = {
  hot1: "bg-sky-50 dark:bg-sky-950/60",
  hot2: "bg-sky-100 dark:bg-sky-900/60",
  hot3: "bg-sky-200 dark:bg-sky-800/60",
};

const REC_CLS: Record<string, string> = {
  red: "bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-400",
  yellow: "bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-400",
  green: "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-400",
  blue: "bg-sky-100 text-sky-700 dark:bg-sky-950 dark:text-sky-400",
};

const CLUSTER_CLS: Record<string, string> = {
  urgent: "bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-400",
  risk: "bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-400",
  rebalance: "bg-sky-100 text-sky-700 dark:bg-sky-950 dark:text-sky-400",
  ok: "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-400",
  muted: "bg-muted text-muted-foreground",
};

function hexToRgba(hex: string, alpha: number): string {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex);
  if (!m) return "";
  const raw = m[1]!;
  const r = parseInt(raw.slice(0, 2), 16);
  const g = parseInt(raw.slice(2, 4), 16);
  const b = parseInt(raw.slice(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

const numCls = "flex items-center justify-end border-l px-2 tabular-nums";

interface Col {
  key: string;
  label: string;
  width: number;
  sortable: boolean;
  align?: "left" | "right";
  cell: (row: ViewRow, m: RowMetrics) => React.ReactNode;
  total?: (t: Totals) => React.ReactNode;
}

interface Totals {
  qty: number[];
  total: number[];
  margin: number[];
  costedQty: number[];
  qtySum: number;
  current: number;
  previous: number;
  previousMtd: number;
  deltaMtd: number;
  last30: number;
  stockMp: number;
  stock1c: number;
  stockTotal: number;
}

function MonthCell({
  qty,
  amount,
  margin,
  costedQty,
  mode,
  maxCell,
}: {
  qty: number;
  amount: number;
  margin: number;
  costedQty: number;
  mode: TableMode;
  maxCell: number;
}): React.ReactElement {
  const heat = HEAT_CLS[heatClass(qty, maxCell)] ?? "";
  if (!qty) return <div className="border-l px-2 py-1" />;
  if (mode === "price") {
    const price = qty ? Math.round(amount / qty) : 0;
    return (
      <div className={cn("border-l px-2 py-0.5 text-right tabular-nums", heat)}>
        <div className="font-medium">{int0(qty)}</div>
        <div className="text-[11px] text-muted-foreground">{int0(price)} ₽ ср.</div>
      </div>
    );
  }
  if (mode === "margin") {
    const known = costedQty > 0;
    const pct = known ? marginPct(margin, amount) : 0;
    return (
      <div className={cn("border-l px-2 py-0.5 text-right tabular-nums", heat)}>
        <div className="font-medium">{int0(qty)}</div>
        <div
          className={cn(
            "text-[11px]",
            !known && "text-muted-foreground",
            known && margin < 0 && "text-red-600 dark:text-red-400",
            known && margin > 0 && "text-emerald-600 dark:text-emerald-400",
          )}
        >
          {known ? `${int0(margin)} ₽ · ${String(pct).replace(".", ",")}%` : "нет фин."}
        </div>
      </div>
    );
  }
  return <div className={cn(numCls, heat)}>{int0(qty)}</div>;
}

function CommentCell({
  article,
  value,
  canEdit,
  onCommit,
}: {
  article: string;
  value: string;
  canEdit: boolean;
  onCommit: (comment: string) => void;
}): React.ReactElement {
  return (
    <div className="flex items-center border-l px-1" onClick={(e) => e.stopPropagation()}>
      <input
        key={`${article}:${value}`}
        defaultValue={value}
        disabled={!canEdit}
        placeholder="Комментарий"
        className="h-7 w-full rounded border-0 bg-transparent px-1 text-[11px] focus:border focus:border-primary focus:bg-background focus:outline-none"
        onBlur={(e) => {
          const v = e.target.value.trim();
          if (v !== value) onCommit(v);
        }}
      />
    </div>
  );
}

interface MatrixTableProps {
  rows: ViewRow[];
  ctx: ViewContext;
  mode: TableMode;
  sort: SortState | null;
  onSortChange: (s: SortState | null) => void;
  extras?: MatrixExtras;
  renderDetail?: (row: ViewRow, m: RowMetrics) => React.ReactNode;
  expandedKey?: string | null;
  onToggleExpand?: (key: string) => void;
}

interface YearGroup {
  year: string;
  months: string[];
}

function yearGroups(months: string[]): YearGroup[] {
  const groups: YearGroup[] = [];
  for (const month of months) {
    const year = month.slice(0, 4);
    const last = groups[groups.length - 1];
    if (last && last.year === year) last.months.push(month);
    else groups.push({ year, months: [month] });
  }
  return groups;
}

function sortValue(row: ViewRow, m: RowMetrics, key: string, months: string[]): number | string {
  switch (key) {
    case "article": return row.article;
    case "name": return row.name;
    case "qty": return row.qtySum;
    case "current": return m.current;
    case "previous": return m.previous;
    case "delta": return m.delta;
    case "previousMtd": return m.previousMtd;
    case "deltaMtd": return m.deltaMtd;
    case "last30": return m.last30;
    case "stock_mp": return row.stockMp;
    case "stock_1c": return row.stock1c;
    case "stock_total": return row.stockTotal;
    case "stockDays": return m.stockDaysSort;
    case "last": return row.lastSale ?? "";
    default: {
      if (key.startsWith("month:")) {
        const idx = months.indexOf(key.slice(6));
        return idx >= 0 ? (row.qty[idx] ?? 0) : 0;
      }
      if (key.startsWith("year:")) {
        const year = key.slice(5);
        return months.reduce(
          (s, month, i) => (month.startsWith(year) ? s + (row.qty[i] ?? 0) : s),
          0,
        );
      }
      return 0;
    }
  }
}

export function MatrixTable({
  rows,
  ctx,
  mode,
  sort,
  onSortChange,
  extras,
  renderDetail,
  expandedKey,
  onToggleExpand,
}: MatrixTableProps): React.ReactElement {
  const months = ctx.months;
  const showMonths = mode !== "compact";
  const groups = useMemo(() => yearGroups(months), [months]);

  const metricsByKey = useMemo(() => {
    const m = new Map<string, RowMetrics>();
    for (const row of rows) m.set(row.key, rowMetrics(row, ctx));
    return m;
  }, [rows, ctx]);

  const sorted = useMemo(() => {
    if (!sort) return rows;
    const dir = sort.dir === "asc" ? 1 : -1;
    return [...rows].sort((a, b) => {
      const va = sortValue(a, metricsByKey.get(a.key)!, sort.key, months);
      const vb = sortValue(b, metricsByKey.get(b.key)!, sort.key, months);
      if (typeof va === "string" || typeof vb === "string") {
        return dir * String(va).localeCompare(String(vb), "ru");
      }
      return dir * (va - vb);
    });
  }, [rows, sort, metricsByKey, months]);

  const maxCell = useMemo(() => Math.max(1, ...rows.flatMap((r) => r.qty)), [rows]);

  const totals = useMemo<Totals>(() => {
    const t: Totals = {
      qty: months.map(() => 0),
      total: months.map(() => 0),
      margin: months.map(() => 0),
      costedQty: months.map(() => 0),
      qtySum: 0,
      current: 0, previous: 0, previousMtd: 0, deltaMtd: 0, last30: 0,
      stockMp: 0, stock1c: 0, stockTotal: 0,
    };
    for (const row of rows) {
      const m = metricsByKey.get(row.key)!;
      for (let i = 0; i < months.length; i += 1) {
        t.qty[i] = (t.qty[i] ?? 0) + (row.qty[i] ?? 0);
        t.total[i] = (t.total[i] ?? 0) + (row.total[i] ?? 0);
        t.margin[i] = (t.margin[i] ?? 0) + (row.margin[i] ?? 0);
        t.costedQty[i] = (t.costedQty[i] ?? 0) + (row.costedQty[i] ?? 0);
      }
      t.qtySum += row.qtySum;
      t.current += m.current;
      t.previous += m.previous;
      t.previousMtd += m.previousMtd;
      t.deltaMtd += m.deltaMtd;
      t.last30 += m.last30;
      t.stockMp += row.stockMp;
      t.stock1c += row.stock1c;
      t.stockTotal += row.stockTotal;
    }
    return t;
  }, [rows, metricsByKey, months]);

  const deltaCls = (v: number): string =>
    v >= 0 ? "text-emerald-600 dark:text-emerald-400" : "text-red-600 dark:text-red-400";

  // ── колонки фиксированной части (первая — sticky, рендерится отдельно) ──
  const cols = useMemo<Col[]>(() => {
    const list: Col[] = [];
    list.push({
      key: "compare", label: "Сравн.", width: 44, sortable: false,
      cell: (row) => (
        <div className="flex items-center justify-center border-l" onClick={(e) => e.stopPropagation()}>
          <input
            type="checkbox"
            checked={extras?.compare.set.has(row.key) ?? false}
            onChange={() => extras?.compare.toggle(row.key)}
            title="Сравнить артикул"
          />
        </div>
      ),
    });
    list.push({
      key: "bulk", label: "Выбор", width: 44, sortable: false,
      cell: (row) => (
        <div className="flex items-center justify-center border-l" onClick={(e) => e.stopPropagation()}>
          <input
            type="checkbox"
            checked={extras?.bulk.set.has(row.article) ?? false}
            onChange={() => extras?.bulk.toggle(row.article)}
            title="Выбрать строку (раскраска)"
          />
        </div>
      ),
    });
    if (extras?.control) {
      list.push({
        key: "control", label: "В контроль", width: 74, sortable: false,
        cell: (row) => (
          <div className="flex items-center justify-center border-l" onClick={(e) => e.stopPropagation()}>
            <input
              type="checkbox"
              disabled={!extras.canEdit}
              checked={extras.control!.set.has(`${extras.control!.mp}::${row.article}`)}
              onChange={(e) =>
                extras.control!.toggle(
                  { article: row.article, name: row.name, brand: row.brand, code_1c: row.code_1c, id_1c: row.id_1c },
                  e.target.checked,
                )
              }
              title="В контроль"
            />
          </div>
        ),
      });
    }
    if (extras) {
      list.push({
        key: "product_type", label: "Тип продукта", width: 100, sortable: false, align: "left",
        cell: (row) => (
          <div className="flex items-center truncate border-l px-2 text-muted-foreground">
            {extras.attrs.get(row.article)?.product_type || ""}
          </div>
        ),
      });
      list.push({
        key: "characteristic", label: "Характеристика", width: 104, sortable: false, align: "left",
        cell: (row) => {
          const name = extras.attrs.get(row.article)?.characteristic || "";
          const color = name ? extras.charColors.get(name) : undefined;
          return (
            <div className="flex items-center gap-1 truncate border-l px-2">
              {color && <span className="size-2 shrink-0 rounded-full" style={{ backgroundColor: color }} />}
              <span className="truncate text-muted-foreground">{name}</span>
            </div>
          );
        },
      });
      list.push({
        key: "comment", label: "Комментарий", width: 150, sortable: false, align: "left",
        cell: (row) => (
          <CommentCell
            article={row.article}
            value={extras.attrs.get(row.article)?.comment ?? ""}
            canEdit={extras.canEdit}
            onCommit={(c) => extras.onComment(row.article, c)}
          />
        ),
      });
    }
    list.push({
      key: "abcxyz", label: "ABC/XYZ", width: 74, sortable: false,
      cell: (row) => <div className={cn(numCls, "text-muted-foreground")}>{row.abcxyz}</div>,
    });
    list.push({
      key: "action", label: "Действие", width: 130, sortable: false,
      cell: (row, m) => {
        const rec = recommendation(row, m);
        return (
          <div className="flex items-center border-l px-1.5" title={rec.note}>
            <span className={cn("truncate rounded px-1.5 py-0.5 text-[11px] font-medium", REC_CLS[rec.cls])}>
              {rec.title}
            </span>
          </div>
        );
      },
    });
    list.push({
      key: "current", label: "Тек. мес", width: 84, sortable: true,
      cell: (_r, m) => <div className={numCls}>{int0(m.current)}</div>,
      total: (t) => int0(t.current),
    });
    if (extras?.plan) {
      const planOf = (article: string): number => extras.plan!.get(article) ?? 0;
      list.push({
        key: "plan_month", label: "План мес", width: 84, sortable: false,
        cell: (row) => {
          const p = planOf(row.article);
          return <div className={cn(numCls, !p && "text-muted-foreground")}>{p ? int0(p) : "—"}</div>;
        },
      });
      list.push({
        key: "plan_pct", label: "Вып.", width: 68, sortable: false,
        cell: (row, m) => {
          const p = planOf(row.article);
          if (!p) return <div className="border-l" />;
          const pct = Math.round((m.current / p) * 100);
          const cls =
            pct >= 100
              ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-400"
              : pct >= 80
                ? "bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-400"
                : "bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-400";
          return (
            <div className="flex items-center justify-end border-l px-1.5">
              <span className={cn("rounded px-1 py-0.5 text-[10px] font-medium", cls)}>{pct}%</span>
            </div>
          );
        },
      });
    }
    list.push({
      key: "previous", label: "Пред. мес", width: 84, sortable: true,
      cell: (_r, m) => <div className={numCls}>{int0(m.previous)}</div>,
      total: (t) => int0(t.previous),
    });
    list.push({
      key: "delta", label: "Δ", width: 84, sortable: true,
      cell: (_r, m) => <div className={cn(numCls, deltaCls(m.delta))}>{signed(m.delta)}</div>,
      total: (t) => (
        <span className={deltaCls(t.current - t.previous)}>{signed(t.current - t.previous)}</span>
      ),
    });
    list.push({
      key: "previousMtd", label: "Прош. MTD", width: 88, sortable: true,
      cell: (_r, m) => <div className={numCls}>{int0(m.previousMtd)}</div>,
      total: (t) => int0(t.previousMtd),
    });
    list.push({
      key: "deltaMtd", label: "Δ MTD", width: 84, sortable: true,
      cell: (_r, m) => <div className={cn(numCls, deltaCls(m.deltaMtd))}>{signed(m.deltaMtd)}</div>,
      total: (t) => <span className={deltaCls(t.deltaMtd)}>{signed(t.deltaMtd)}</span>,
    });
    list.push({
      key: "last30", label: "30 дней", width: 80, sortable: true,
      cell: (_r, m) => <div className={numCls}>{int0(m.last30)}</div>,
      total: (t) => int0(t.last30),
    });
    list.push({
      key: "stock_mp", label: "Ост. МП", width: 80, sortable: true,
      cell: (row) => <div className={numCls}>{int0(row.stockMp)}</div>,
      total: (t) => int0(t.stockMp),
    });
    list.push({
      key: "stock_1c", label: "Ост. 1С", width: 76, sortable: true,
      cell: (row) => <div className={numCls}>{int0(row.stock1c)}</div>,
      total: (t) => int0(t.stock1c),
    });
    list.push({
      key: "stock_total", label: "Совокупно", width: 88, sortable: true,
      cell: (row) => <div className={cn(numCls, "font-medium")}>{int0(row.stockTotal)}</div>,
      total: (t) => int0(t.stockTotal),
    });
    list.push({
      key: "stockDays", label: "Запас, дн", width: 84, sortable: true,
      cell: (_r, m) => {
        const range = stockDayRange(m.stockDays);
        return (
          <div
            className={cn(numCls, "font-medium")}
            style={{ backgroundColor: `${range.color}26` }}
            title={range.label}
          >
            {m.stockDays === null ? "—" : int0(m.stockDays)}
          </div>
        );
      },
    });
    if (extras?.clusters) {
      list.push({
        key: "clusters", label: "Кластеры", width: 120, sortable: false,
        cell: (row) => {
          const data = extras.clusters!;
          const sig = clusterRowSignal(
            data.byArticle.get(row.article) ?? [],
            data.rules,
            data.selectedDays,
            extras.control?.mp ?? "all",
          );
          const title = sig.clusters
            .slice(0, 8)
            .map(
              (c) =>
                `${c.cluster.name} (${c.mp}): продажи ${int0(c.sales)}, остаток ${int0(c.stock)}, ` +
                `${c.stockDays === null ? "нет продаж" : `${c.stockDays} дн`} · ${c.action}`,
            )
            .join("\n");
          return (
            <div className="flex items-center border-l px-1.5" onClick={(e) => e.stopPropagation()}>
              <span
                className={cn("truncate rounded px-1.5 py-0.5 text-[10px] font-medium", CLUSTER_CLS[sig.status])}
                title={title || sig.note}
              >
                {sig.label}
              </span>
            </div>
          );
        },
      });
    }
    list.push({
      key: "last", label: "Посл. продажа", width: 104, sortable: true,
      cell: (row) => <div className={cn(numCls, "text-muted-foreground")}>{fmtDate(row.lastSale)}</div>,
    });
    list.push({
      key: "trend", label: "Тренд", width: 84, sortable: false,
      cell: (row) => (
        <div className="flex items-center border-l px-1.5">
          <Sparkline buckets={row.spark} />
        </div>
      ),
    });
    return list;
  }, [extras]);

  const gridTemplate = useMemo(() => {
    const fixed = [`280px`, ...cols.map((c) => `${c.width}px`)];
    const monthCols = showMonths
      ? groups.flatMap((g) => [...g.months.map(() => `${MONTH_W}px`), `${YEAR_TOTAL_W}px`])
      : [];
    return [...fixed, ...monthCols, `${TOTAL_W}px`].join(" ");
  }, [cols, groups, showMonths]);

  const parentRef = useRef<HTMLDivElement>(null);
  const [viewW, setViewW] = React.useState(0);
  React.useEffect(() => {
    const measure = (): void => setViewW(parentRef.current?.clientWidth ?? 0);
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);
  const virt = useVirtualizer({
    count: sorted.length,
    getScrollElement: () => parentRef.current,
    estimateSize: (index) =>
      renderDetail && expandedKey === sorted[index]?.key ? ROW_H * 9 : ROW_H,
    overscan: 12,
  });
  React.useEffect(() => virt.measure(), [expandedKey, sort, virt]);

  const clickSort = (key: string, sortable: boolean): void => {
    if (!sortable) return;
    if (!sort || sort.key !== key) onSortChange({ key, dir: "desc" });
    else if (sort.dir === "desc") onSortChange({ key, dir: "asc" });
    else onSortChange(null);
  };

  const sortIcon = (key: string): React.ReactNode => {
    if (sort?.key !== key) return null;
    return sort.dir === "desc" ? (
      <ChevronDown className="inline size-3" />
    ) : (
      <ChevronUp className="inline size-3" />
    );
  };

  const headerCell = (label: string, key: string, sortable: boolean, alignLeft = false): React.ReactElement => (
    <button
      key={key}
      type="button"
      onClick={() => clickSort(key, sortable)}
      className={cn(
        "flex items-end justify-end gap-0.5 border-l px-2 py-1 text-right text-[11px] font-medium text-muted-foreground",
        sortable && "cursor-pointer hover:text-foreground",
        alignLeft && "justify-start text-left",
      )}
    >
      {label} {sortIcon(key)}
    </button>
  );

  /** Заливка строки: ручная подсветка приоритетнее цвета характеристики (порт rowHighlightStyle). */
  const rowTint = (row: ViewRow): { bg?: string; bar?: string } => {
    if (!extras) return {};
    const manual = extras.highlights.get(row.article);
    if (manual) return { bg: hexToRgba(manual, 0.2), bar: manual };
    const name = extras.attrs.get(row.article)?.characteristic;
    const color = name ? extras.charColors.get(name) : undefined;
    if (color) return { bg: hexToRgba(color, 0.16), bar: color };
    return {};
  };

  return (
    <div
      ref={parentRef}
      className="max-h-[calc(100vh-260px)] overflow-auto rounded-md border"
    >
      <div className="min-w-max">
        {/* Шапка: годы + колонки */}
        <div className="sticky top-0 z-20 bg-background shadow-[0_1px_0_0_hsl(var(--border))]">
          {showMonths && (
            <div className="grid" style={{ gridTemplateColumns: gridTemplate }}>
              <div
                className="sticky left-0 z-10 bg-background"
                style={{ gridColumn: `span ${cols.length + 1}` }}
              />
              {groups.map((g) => (
                <div
                  key={g.year}
                  className="border-l px-2 py-0.5 text-center text-[11px] font-semibold text-muted-foreground"
                  style={{ gridColumn: `span ${g.months.length + 1}` }}
                >
                  {g.year}
                </div>
              ))}
              <div />
            </div>
          )}
          <div className="grid" style={{ gridTemplateColumns: gridTemplate }}>
            <div className="sticky left-0 z-10 bg-background">
              {headerCell("Артикул / название", "article", true, true)}
            </div>
            {cols.map((c) => headerCell(c.label, c.key, c.sortable, c.align === "left"))}
            {showMonths &&
              groups.flatMap((g) => [
                ...g.months.map((month) => headerCell(monthLabel(month), `month:${month}`, true)),
                headerCell(`Σ ${g.year}`, `year:${g.year}`, true),
              ])}
            {headerCell("Итого", "qty", true)}
          </div>
          {/* ИТОГО */}
          <div
            className="grid border-t bg-muted/60 text-xs font-semibold"
            style={{ gridTemplateColumns: gridTemplate }}
          >
            <div className="sticky left-0 z-10 bg-muted px-2 py-1.5">
              ИТОГО · {int0(totals.qtySum)} шт
            </div>
            {cols.map((c) => (
              <div key={c.key} className="border-l px-2 py-1.5 text-right tabular-nums">
                {c.total ? c.total(totals) : ""}
              </div>
            ))}
            {showMonths &&
              groups.flatMap((g) => [
                ...g.months.map((month) => {
                  const i = months.indexOf(month);
                  return (
                    <div key={month} className="border-l px-2 py-1.5 text-right tabular-nums">
                      {totals.qty[i] ? int0(totals.qty[i]!) : ""}
                    </div>
                  );
                }),
                <div key={`y${g.year}`} className="border-l px-2 py-1.5 text-right tabular-nums font-bold">
                  {int0(g.months.reduce((s, month) => s + (totals.qty[months.indexOf(month)] ?? 0), 0))}
                </div>,
              ])}
            <div className="border-l px-2 py-1.5 text-right font-bold tabular-nums">
              {int0(totals.qtySum)}
            </div>
          </div>
        </div>

        {/* Виртуализированные строки */}
        <div style={{ height: virt.getTotalSize(), position: "relative" }}>
          {virt.getVirtualItems().map((vi) => {
            const row = sorted[vi.index];
            if (!row) return null;
            const m = metricsByKey.get(row.key)!;
            const expanded = renderDetail && expandedKey === row.key;
            const tint = rowTint(row);
            return (
              <div
                key={row.key}
                className="absolute left-0 w-full border-b text-xs"
                style={{ top: vi.start }}
                ref={virt.measureElement}
                data-index={vi.index}
              >
                <div
                  className={cn(
                    "grid items-stretch transition-colors hover:bg-accent/40",
                    onToggleExpand && "cursor-pointer",
                  )}
                  style={{
                    gridTemplateColumns: gridTemplate,
                    height: ROW_H,
                    ...(tint.bg ? { backgroundColor: tint.bg } : {}),
                  }}
                  onClick={() => onToggleExpand?.(row.key)}
                >
                  <div
                    className="sticky left-0 z-10 flex flex-col justify-center overflow-hidden bg-background px-2"
                    style={tint.bar ? { boxShadow: `inset 3px 0 0 0 ${tint.bar}` } : undefined}
                  >
                    <div className="truncate font-medium">{row.article}</div>
                    <div className="truncate text-[11px] text-muted-foreground" title={row.name}>
                      {row.name}
                    </div>
                  </div>
                  {cols.map((c) => (
                    <React.Fragment key={c.key}>{c.cell(row, m)}</React.Fragment>
                  ))}
                  {showMonths &&
                    groups.flatMap((g) => [
                      ...g.months.map((month) => {
                        const i = months.indexOf(month);
                        return (
                          <MonthCell
                            key={month}
                            qty={row.qty[i] ?? 0}
                            amount={row.total[i] ?? 0}
                            margin={row.margin[i] ?? 0}
                            costedQty={row.costedQty[i] ?? 0}
                            mode={mode}
                            maxCell={maxCell}
                          />
                        );
                      }),
                      <div
                        key={`y${g.year}`}
                        className="flex items-center justify-end border-l bg-muted/40 px-2 font-medium tabular-nums"
                      >
                        {int0(
                          g.months.reduce(
                            (s, month) => s + (row.qty[months.indexOf(month)] ?? 0),
                            0,
                          ),
                        )}
                      </div>,
                    ])}
                  <div className="flex items-center justify-end border-l px-2 font-semibold tabular-nums">
                    {int0(row.qtySum)}
                  </div>
                </div>
                {expanded && renderDetail && (
                  <div
                    className="sticky left-0 border-t bg-muted/30"
                    style={viewW ? { width: viewW } : undefined}
                  >
                    {renderDetail(row, m)}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
